// react import
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Resizable } from "re-resizable";
import { useSelector } from "react-redux";
import CommonCell from "../common/CommonCell";
import { MaximumIcon } from "../icon/Icons";
import { MinimumIcon } from "../icon/Icons";

const headerStyle = {
  minWidth: "17%",
  width: "17%",
  background: "#f5f5f5",
  color: "#616161",
};

const DragBoard = ({
  children,
  left = 0,
  top = 0,
  defaultSize,
  footerContents,
  minWidth,
  minHeight,
  zIndex,
  hideHandler,
  enable,
}) => {
  const wrapRef = useRef();
  const [wrapLeft, setWrapLeft] = useState(left);
  const [wrapTop, setWrapTop] = useState(top);

  const [size, setSize] = useState(defaultSize);

  useEffect(() => {
    setSize(defaultSize);
  }, [defaultSize]);

  const clickX = useRef(0);
  const clickY = useRef(0);

  const { boardDisplay, workMode, opacityOn } = useSelector(
    state => state.GameSlice
  );
  const [drag, setDrag] = useState(true);
  const [opacity, setOpacity] = useState(100);
  const [hide, setHide] = useState(false);

  useEffect(() => {
    setHide(workMode);
  }, [workMode]);
  useEffect(() => {
    if (opacityOn) {
      setOpacity(100);
    } else {
      setOpacity(0);
    }
  }, [opacityOn]);

  useEffect(() => {
    if (!drag) {
      const downHandler = e => {
        if (wrapRef.current.contains(e.target)) {
          clickX.current = e.clientX;
          clickY.current = e.clientY;
        }

        document.addEventListener("mouseup", upHandler);
        document.addEventListener("mousemove", moveHandler);
      };

      const moveHandler = e => {
        if (clickX.current && clickY.current) {
          const moveX = e.clientX - clickX.current;
          const moveY = e.clientY - clickY.current;
          if (moveX && moveY) {
            const leftValue = wrapLeft + moveX;
            const topValue = wrapTop + moveY;

            setWrapLeft(leftValue > 0 ? leftValue : 0);
            setWrapTop(topValue > 0 ? topValue : 0);
          }
        }
      };

      const upHandler = e => {
        clickX.current = 0;
        clickY.current = 0;
        document.removeEventListener("mouseup", upHandler);
        document.removeEventListener("mousemove", moveHandler);
      };

      document.addEventListener("mousedown", downHandler);

      return () => {
        document.removeEventListener("mousedown", downHandler);
      };
    }
  }, [wrapLeft, wrapTop, drag]);

  return (
    !boardDisplay && (
      <DragContainer
        $left={wrapLeft}
        $top={wrapTop}
        ref={wrapRef}
        $zIndex={zIndex}>
        <Resizable
          enable={
            enable || {
              top: false,
              right: true,
              bottom: true,
              left: false,
              topRight: true,
              bottomRight: true,
              bottomLeft: false,
              topLeft: false,
            }
          }
          onResizeStop={(e, direction, ref, d) => {
            setSize({
              width: size.width + d.width,
              height: size.height + d.height,
            });
          }}
          size={size}
          defaultSize={defaultSize || { width: "400px", height: "400px" }}
          minWidth={minWidth || "400px"}
          minHeight={minHeight || "400px"}>
          <BorderWrap $opacity={opacity}>
            <DragHeader
              onMouseEnter={() => setDrag(false)}
              onMouseLeave={() => setDrag(true)}>
              <CommonCell styles={headerStyle}>A</CommonCell>
              <CommonCell styles={headerStyle}>B</CommonCell>
              <CommonCell styles={headerStyle}>C</CommonCell>
              <CommonCell styles={headerStyle}>D</CommonCell>
              <CommonCell styles={headerStyle}>E</CommonCell>
              <CommonCell
                styles={{
                  minWidth: "15%",
                  width: "15%",
                  background: "#f5f5f5",
                  gap: "6px",
                  justifyContent: "end",
                }}>
                <span
                  style={{
                    cursor: "pointer",
                    translate: "0 2px",
                  }}
                  onClick={() => {
                    setHide(pre => !pre);
                    hideHandler?.(pre => !pre);
                  }}>
                  {hide ? <MaximumIcon /> : <MinimumIcon />}
                </span>
              </CommonCell>
            </DragHeader>
            {!hide && (
              <>
                <Wrap>{children}</Wrap>
                <Footer>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <OpacityRange
                      type="range"
                      min={1}
                      max={100}
                      onChange={e => setOpacity(Number(e.target.value))}
                      value={opacity}
                    />
                  </div>
                  {footerContents}
                </Footer>
              </>
            )}
          </BorderWrap>
        </Resizable>
      </DragContainer>
    )
  );
};

export default DragBoard;

const DragContainer = styled.div`
  position: absolute;
  top: ${p => p.$top}px;
  left: ${p => p.$left}px;
  z-index: ${p => p.$zIndex};
`;

const BorderWrap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e0e0e0;
  opacity: ${({ $opacity }) => 0.2 + 0.8 * ($opacity / 100)};
`;

const DragHeader = styled.div`
  display: flex;
  border-left: 1px solid #e0e0e0;
`;

const Wrap = styled.div`
  display: flex;
  height: calc(100% - 45px);
  background-color: white;
  z-index: 1;
`;

const Footer = styled.div`
  width: 100%;
  height: 24px;
  padding-left: 20px;
  display: flex;
  justify-content: space-between;
  background-color: #e7e7e7;
  z-index: 2;
`;

const OpacityRange = styled.input`
  width: 60px;
  height: 1px;
  background-color: #8a8a8a;

  -webkit-appearance: none;
  appearance: input;
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    background: #fff;
    width: 12px;
    height: 12px;
    border: 1px solid #bababa;
    border-radius: 6px;
    cursor: pointer;
  }
`;
